'use client';

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { merge } from 'lodash';

import { useConfigValue } from '@/components/providers/config-provider';
import { useLocaleMessages } from '@/components/providers/locale-messages-provider';
import { fetchWithTimeout } from '@/lib/utils/api-client';

// A modal waits on this before it can open, so it fails fast rather than
// leaving the visitor with no dismissable dialog.
const FETCH_TIMEOUT = 5000;

// Inline translations are replaced by the deploy that changes them. This URL is
// cached long enough to outlive one, so it carries the build stamp to be
// invalidated by a deploy on the same terms instead of on its own schedule.
const BUILD_STAMP = process.env.NEXT_PUBLIC_BUILD_TIMESTAMP ?? 'dev';

type Dictionary = Record<string, unknown>;

interface LocaleDictionaryResult {
  dictionary: Dictionary | null;
  settled: boolean;
}

interface DictionaryEntry {
  status: 'loading' | 'ok' | 'failed';
  result: LocaleDictionaryResult;
}

const PENDING_RESULT: LocaleDictionaryResult = { dictionary: null, settled: false };
const FAILED_RESULT: LocaleDictionaryResult = { dictionary: null, settled: true };
const ABSENT_RESULT: LocaleDictionaryResult = { dictionary: null, settled: true };

// Keyed by origin as well as locale: two assistants on one page can be
// configured against different API origins, and their dictionaries differ.
const entries = new Map<string, DictionaryEntry>();
const subscribers = new Map<string, Set<() => void>>();

interface DictionaryRequest {
  baseUrl: string;
  locale: string;
}

function cacheKey({ baseUrl, locale }: DictionaryRequest): string {
  return `${baseUrl}|${locale}`;
}

function setDictionaryEntry(key: string, entry: DictionaryEntry) {
  entries.set(key, entry);
  for (const notify of subscribers.get(key) ?? []) notify();
}

function subscribeToDictionary(key: string, onStoreChange: () => void): () => void {
  let callbacks = subscribers.get(key);
  if (!callbacks) {
    callbacks = new Set();
    subscribers.set(key, callbacks);
  }
  callbacks.add(onStoreChange);

  return function unsubscribe() {
    callbacks.delete(onStoreChange);
  };
}

function getDictionarySnapshot(key: string): LocaleDictionaryResult {
  return entries.get(key)?.result ?? PENDING_RESULT;
}

function getServerSnapshot(): LocaleDictionaryResult {
  return PENDING_RESULT;
}

async function loadDictionary({ baseUrl, locale }: DictionaryRequest) {
  const key = cacheKey({ baseUrl, locale });

  // A failure is retried by the next mount; a success and an in-flight request
  // are both already the answer this consumer is waiting for.
  const entry = entries.get(key);
  if (entry && entry.status !== 'failed') return;

  setDictionaryEntry(key, { status: 'loading', result: PENDING_RESULT });

  try {
    // The deadline has to outlive the headers: fetchWithTimeout drops its own
    // abort timer as soon as they arrive, so a stalled body would otherwise
    // never settle and the dialog would never open.
    const response = await fetchWithTimeout({
      url: `${baseUrl}/api/messages/${locale}?v=${BUILD_STAMP}`,
      options: { signal: AbortSignal.timeout(FETCH_TIMEOUT) },
      timeout: FETCH_TIMEOUT,
      retries: 0,
    });
    const dictionary = response.ok ? ((await response.json()) as Dictionary) : null;
    setDictionaryEntry(
      key,
      dictionary
        ? { status: 'ok', result: { dictionary, settled: true } }
        : { status: 'failed', result: FAILED_RESULT },
    );
  } catch {
    setDictionaryEntry(key, { status: 'failed', result: FAILED_RESULT });
  }
}

export function resetLocaleDictionaryCache() {
  entries.clear();
  subscribers.clear();
}

/**
 * Resolves one locale's full dictionary, preferring dictionaries already inlined
 * in context (the standalone package) over a request to the assistant's route.
 */
export function useLocaleDictionary(locale: string | undefined): LocaleDictionaryResult {
  const inlinedByLocale = useLocaleMessages();
  const baseUrl = useConfigValue<string>('apiBaseUrl') ?? '';

  const inlined = useMemo(() => {
    if (!locale) return null;
    const localized = inlinedByLocale[locale];
    if (!localized) return null;
    if (locale === 'en') return localized;
    return merge({} as Dictionary, inlinedByLocale['en'] ?? {}, localized);
  }, [inlinedByLocale, locale]);

  const hasInlined = inlined !== null;
  const key = locale ? cacheKey({ baseUrl, locale }) : '';

  const subscribe = useCallback(
    (onStoreChange: () => void) => subscribeToDictionary(key, onStoreChange),
    [key],
  );
  const getSnapshot = useCallback(() => getDictionarySnapshot(key), [key]);
  const loaded = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    if (!locale || hasInlined) return;
    loadDictionary({ baseUrl, locale });
  }, [baseUrl, locale, hasInlined]);

  if (!locale) return ABSENT_RESULT;
  if (inlined) return { dictionary: inlined, settled: true };
  return loaded;
}
